import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import Search from '../views/Search.vue'
import ProviderDetail from '../views/ProviderDetail.vue'
import Login from '../views/Login.vue'
import Register from '../views/Register.vue'
import Verify from '../views/Verify.vue'
import { isAuthenticated, isPatient, isProvider, isValidator, isAdmin } from '../services/auth'
import Settings from '../views/Settings.vue'
import About from '@/views/About.vue'
import Bookmarks from '@/views/Bookmarks.vue'
import OAuthCallbackView from '@/views/OAuthCallbackView.vue'
import ProviderProfile from '@/views/ProviderProfile.vue'
import Features from '@/views/Features.vue'
import ManageReview from '@/views/ManageReview.vue'
import Reviews from '@/views/Reviews.vue'
import PatientProfile from '@/views/PatientProfile.vue'
import PatientProfileEdit from '@/views/PatientProfileEdit.vue'
import PatientDocuments from '@/views/PatientDocuments.vue'
import ValidatorProfile from '@/views/ValidatorProfile.vue'
import ClaimProfile from '@/views/ClaimProfile.vue'
import Schedules from '@/views/Schedules.vue'
import ProvidersHome from '@/views/ProvidersHome.vue'
import PatientsHome from '@/views/PatientsHome.vue'
import ManageProvider from '@/views/ManageProvider.vue'
import ManageProviderImages from '@/views/ManageProviderImages.vue'
import ProviderEdit from '@/views/ProviderEdit.vue'
import ValidatorSearch from '@/views/ValidatorSearch.vue'
import AdminPage from '@/views/AdminPage.vue'
import Specializations from '@/views/Specializations.vue'
import PatientChat from '@/views/PatientChat.vue'
import ProviderChat from '@/views/ProviderChat.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/patients',
    name: 'Patients',
    component: PatientsHome
  },
  {
    path: '/providers',
    name: 'Providers',
    component: ProvidersHome
  },
  {
    path: '/features',
    name: 'Features',
    component: Features
  },
  {
    path: '/specializations',
    name: 'Specializations',
    component: Specializations
  },
  {
    path: '/about',
    name: 'About',
    component: About
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { guest: true }
  },
  {
    path: '/test-register',
    name: 'test-register',
    component: () => import('../views/TestRegister.vue')
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: { guest: true }
  },
  {
    path: '/auth/oauth/:provider/callback',
    name: 'oauth-callback',
    component: OAuthCallbackView,
    meta: { guest: true }
  },
  {
    path: '/verify',
    name: 'verify',
    component: Verify,
    meta: { guest: true }
  },
  {
    path: '/bookmarks',
    name: 'bookmarks',
    component: Bookmarks,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin-dashboard',
    name: 'AdminDashboard',
    component: AdminPage,
    meta: { requiresAuth: true, requiresAdminRole: true }
  },
  {
    path: '/patient-profile',
    name: 'patientProfile',
    component: PatientProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/patient-profile/edit',
    name: 'patientProfileEdit',
    component: PatientProfileEdit,
    meta: { requiresAuth: true }
  },
  {
    path: '/patient-documents',
    name: 'patientDocuments',
    component: PatientDocuments,
    meta: { requiresAuth: true }
  },
  {
    path: '/patient-chat',
    name: 'PatientChat',
    component: PatientChat,
    meta: { requiresAuth: true, requiresPatientRole: true }
  },
  {
    path: '/provider-chat',
    name: 'ProviderChat',
    component: ProviderChat,
    meta: { requiresAuth: true, requiresProviderRole: true }
  },
  {
    path: '/chat',
    beforeEnter: (to, from, next) => {
      if (isProvider.value) {
        next('/provider-chat')
      } else {
        next('/patient-chat')
      }
    }
  },
  {
    path: '/provider-profile',
    name: 'providerProfile',
    component: ProviderProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/provider/schedules',
    name: 'ProviderSchedules',
    component: Schedules,
    meta: { requiresAuth: true }
  },
  {
    path: '/provider/manage',
    name: 'CreateProvider',
    component: ManageProvider,
    meta: { requiresAuth: true, requiresProviderRole: true }
  },
  {
    path: '/provider/manage/:id',
    name: 'EditProvider',
    component: ManageProvider,
    meta: { requiresAuth: true, requiresProviderRole: true },
    props: true
  },
  {
    path: '/provider/images/:id',
    name: 'ManageProviderImages',
    component: ManageProviderImages,
    meta: { requiresAuth: true, requiresProviderRole: true },
    props: true
  },
  {
    path: '/search',
    name: 'Search',
    component: Search
  },
  {
    path: '/validator-profile',
    name: 'ValidatorProfile',
    component: ValidatorProfile,
    meta: {
      requiresAuth: true,
      requiresValidatorRole: true
    }
  },
  {
    path: '/validator-search',
    name: 'ValidatorSearch',
    component: ValidatorSearch,
    meta: {
      requiresAuth: true,
      requiresValidatorRole: true
    }
  },
  {
    path: '/provider-edit/:id?',
    name: 'ProviderEdit',
    component: ProviderEdit,
    meta: {
      requiresAuth: true,
      requiresValidatorRole: true
    },
    props: true
  },
  {
    path: '/validator-provider-update/:id',
    name: 'ValidatorProvider',
    component: ProviderEdit,
    meta: { requiresAuth: true, requiresValidatorRole: true },
    props: true
  },
  {
    path: '/providers/:id',
    name: 'provider-detail',
    component: ProviderDetail,
    props: true
  },
  {
    path: '/providers/:id/reviews',
    name: 'reviews',
    component: Reviews
  },
  {
    path: '/providers/:id/review/create',
    name: 'CreateReview',
    component: ManageReview,
    meta: { requiresAuth: true }
  },
  {
    path: '/providers/:id/claim',
    name: 'ClaimProfile',
    component: ClaimProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: { requiresAuth: true }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/AccessDenied.vue')
  },
  {
    path: '/server-error',
    name: 'ServerError',
    component: () => import('@/views/ServerError.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // Check for protected routes
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated.value) {
      next({
        path: '/login',
        query: { returnUrl: to.fullPath }
      });
    } else {
      // Check if route requires provider role
      if (to.matched.some(record => record.meta.requiresProviderRole)) {
        if (!isProvider.value) {
          // Redirect to access denied page with reason
          next({
            path: '/access-denied',
            query: { accessDenied: 'provider' }
          });
        } else {
          next();
        }
      }
      // Check if route requires provider role
      else if (to.matched.some(record => record.meta.requiresPatientRole)) {
        if (!isPatient.value) {
          // Redirect to access denied page with reason
          next({
            path: '/access-denied',
            query: { accessDenied: 'patient' }
          });
        } else {
          next();
        }
      }
      // Check if route requires validator role
      else if (to.matched.some(record => record.meta.requiresValidatorRole)) {
        if (!isValidator.value) {
          // Redirect to access denied page with reason
          next({
            path: '/access-denied',
            query: { accessDenied: 'validator' }
          });
        } else {
          next();
        }
      }
      // Check if route requires admin role
      else if (to.matched.some(record => record.meta.requiresAdminRole)) {
        if (!isAdmin.value) {
          // Redirect to access denied page with reason
          next({
            path: '/access-denied',
            query: { accessDenied: 'admin' }
          });
        } else {
          next();
        }
      }
      else {
        next();
      }
    }
  }
  // Check for guest routes (already logged in users shouldn't access login/register)
  else if (to.matched.some(record => record.meta.guest)) {
    if (isAuthenticated.value) {
      next({ path: '/' });
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router